import Chart from "chart.js/auto";

/**
 * Simulateur CO2
 */

const initCo2Simulator = () => {
    const simulator = document.querySelector("[data-co2-simulator]");

    if (!simulator) {
        return;
    }

    /**
     * ------------------------------------------------------
     * CONSTANTES
     * ------------------------------------------------------
     */

    const FE_B7 = 3.1;
    const FE_B100 = 1.21;
    const FE_HVO100 = 0.54;

    const B100_OVERCONSUMPTION = 0.06;

    /**
     * ------------------------------------------------------
     * ÉLÉMENTS
     * ------------------------------------------------------
     */

    const formStep = simulator.querySelector("[data-co2-form]");

    const resultStep = simulator.querySelector("[data-co2-result]");

    const litresForm = simulator.querySelector("[data-co2-litres-form]");

    const distanceForm = simulator.querySelector("[data-co2-distance-form]");

    const tabs = simulator.querySelectorAll("[data-co2-mode]");

    const sourceLabel = simulator.querySelector("[data-co2-source-label]");

    const sourceValue = simulator.querySelector("[data-co2-source-value]");

    const b100Value = simulator.querySelector("[data-co2-b100]");

    const hvo100Value = simulator.querySelector("[data-co2-hvo100]");

    const b100SavedValue = simulator.querySelector("[data-co2-b100-saved]");

    const hvo100SavedValue = simulator.querySelector("[data-co2-hvo100-saved]");

    const restartButton = simulator.querySelector("[data-co2-restart]");

    const downloadButton = simulator.querySelector("[data-co2-download]");

    const chartCanvas = simulator.querySelector("#co2-result-chart");

    /**
     * ------------------------------------------------------
     * MODALE PDF
     * ------------------------------------------------------
     */

    const modal = simulator.querySelector("[data-co2-modal]");

    const modalCloseButtons = simulator.querySelectorAll("[data-co2-modal-close]");

    const emailForm = simulator.querySelector("[data-co2-email-form]");

    const lastNameInput = simulator.querySelector("#co2-last-name");

    const firstNameInput = simulator.querySelector("#co2-first-name");

    const companyInput = simulator.querySelector("#co2-company");

    const emailInput = simulator.querySelector("#co2-email");

    const consentInput = emailForm ? emailForm.querySelector('[name="consent"]') : null;

    const emailSubmitButton = simulator.querySelector("[data-co2-email-submit]");

    const submitLabel = simulator.querySelector("[data-co2-submit-label]");

    const submitLoading = simulator.querySelector("[data-co2-submit-loading]");

    const formMessage = simulator.querySelector("[data-co2-form-message]");

    /**
     * ------------------------------------------------------
     * ÉTAT
     * ------------------------------------------------------
     */

    let chart = null;
    let currentResult = null;
    let isSubmitting = false;

    /**
     * ------------------------------------------------------
     * FORMATAGE
     * ------------------------------------------------------
     */

    const formatNumber = (value) => {
        if (!Number.isFinite(value)) {
            return "0";
        }

        let decimals = 2;

        if (value < 0.01) {
            decimals = 4;
        }

        return new Intl.NumberFormat("fr-FR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: decimals,
        }).format(value);
    };

    /**
     * ------------------------------------------------------
     * CHANGEMENT DE MODE
     * ------------------------------------------------------
     */

    const setMode = (mode) => {
        tabs.forEach((tab) => {
            const isActive = tab.dataset.co2Mode === mode;

            tab.classList.toggle("is-active", isActive);

            tab.setAttribute("aria-selected", isActive ? "true" : "false");
        });

        if (mode === "distance") {
            litresForm.classList.add("hidden");

            distanceForm.classList.remove("hidden");
        } else {
            distanceForm.classList.add("hidden");

            litresForm.classList.remove("hidden");
        }
    };

    tabs.forEach((tab) => {
        tab.addEventListener("click", () => {
            setMode(tab.dataset.co2Mode);
        });
    });

    /**
     * ------------------------------------------------------
     * CALCUL LITRES
     * ------------------------------------------------------
     */

    const calculateFromLitres = (litres) => {
        const b7 = (litres * FE_B7) / 1000;

        const b100Litres = litres * (1 + B100_OVERCONSUMPTION);

        const b100 = (b100Litres * FE_B100) / 1000;

        const hvo100 = (litres * FE_HVO100) / 1000;

        const b100Saved = Math.max(0, b7 - b100);

        const hvo100Saved = Math.max(0, b7 - hvo100);

        return {
            mode: "litres",
            input: litres,
            litres,
            b7,
            b100,
            hvo100,
            b100Saved,
            hvo100Saved,
        };
    };

    /**
     * ------------------------------------------------------
     * CALCUL DISTANCE
     * ------------------------------------------------------
     */

    const calculateFromDistance = (distance, consumption) => {
        const dieselLitres = (distance * consumption) / 100;

        const b100Litres = dieselLitres * (1 + B100_OVERCONSUMPTION);

        const b7 = (dieselLitres * FE_B7) / 1000;

        const b100 = (b100Litres * FE_B100) / 1000;

        const hvo100 = (dieselLitres * FE_HVO100) / 1000;

        const b100Saved = Math.max(0, b7 - b100);

        const hvo100Saved = Math.max(0, b7 - hvo100);

        return {
            mode: "distance",
            input: distance,
            consumption,
            litres: dieselLitres,
            b7,
            b100,
            hvo100,
            b100Saved,
            hvo100Saved,
        };
    };

    /**
     * ------------------------------------------------------
     * GRAPHIQUE
     * ------------------------------------------------------
     */

    const renderChart = (result) => {
        if (!chartCanvas) {
            return;
        }

        if (chart) {
            chart.destroy();
            chart = null;
        }

        const context = chartCanvas.getContext("2d");

        if (!context) {
            return;
        }

        chart = new Chart(context, {
            type: "bar",

            data: {
                labels: ["HVO100", "B100", "Diesel B7"],

                datasets: [
                    {
                        data: [result.hvo100, result.b100, result.b7],

                        backgroundColor: ["#24AE9B", "#F9D64D", "#D1D1D1"],

                        borderWidth: 0,

                        borderRadius: {
                            topLeft: 14,
                            topRight: 14,
                            bottomLeft: 0,
                            bottomRight: 0,
                        },

                        borderSkipped: false,

                        maxBarThickness: 90,
                        categoryPercentage: 0.75,
                        barPercentage: 0.8,
                    },
                ],
            },

            options: {
                responsive: true,

                maintainAspectRatio: false,

                animation: {
                    duration: 500,
                },

                layout: {
                    padding: {
                        top: 25,
                        right: 15,
                        bottom: 0,
                        left: 10,
                    },
                },

                plugins: {
                    legend: {
                        display: false,
                    },

                    tooltip: {
                        callbacks: {
                            label(context) {
                                return `${formatNumber(context.raw)} tCO2eq`;
                            },
                        },
                    },
                },

                scales: {
                    x: {
                        grid: {
                            display: false,
                        },

                        border: {
                            color: "rgba(21, 39, 36, 0.25)",
                        },

                        ticks: {
                            color: "#152724",

                            font: {
                                family: "Montserrat",
                                size: 14,
                                weight: "700",
                            },
                        },
                    },

                    y: {
                        beginAtZero: true,

                        grid: {
                            color: "rgba(21, 39, 36, 0.12)",
                        },

                        border: {
                            display: false,
                        },

                        ticks: {
                            color: "#152724",

                            font: {
                                family: "Montserrat",
                                size: 12,
                            },

                            callback(value) {
                                return formatNumber(value);
                            },
                        },
                    },
                },
            },
        });
    };

    /**
     * ------------------------------------------------------
     * AFFICHAGE RÉSULTAT
     * ------------------------------------------------------
     */

    const showResult = (result) => {
        currentResult = result;

        if (result.mode === "distance") {
            sourceLabel.textContent = "Distance parcourue";

            sourceValue.textContent = `${formatNumber(result.input)} km`;
        } else {
            sourceLabel.textContent = "Consommation en L";

            sourceValue.textContent = formatNumber(result.input);
        }

        if (b100Value) {
            b100Value.textContent = formatNumber(result.b100);
        }

        if (hvo100Value) {
            hvo100Value.textContent = formatNumber(result.hvo100);
        }

        if (b100SavedValue) {
            b100SavedValue.textContent = formatNumber(result.b100Saved);
        }

        if (hvo100SavedValue) {
            hvo100SavedValue.textContent = formatNumber(result.hvo100Saved);
        }

        formStep.classList.add("hidden");

        resultStep.classList.remove("hidden");

        requestAnimationFrame(() => {
            renderChart(result);
        });

        resultStep.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    /**
     * ------------------------------------------------------
     * FORMULAIRE LITRES
     * ------------------------------------------------------
     */

    litresForm.addEventListener("submit", (event) => {
        event.preventDefault();

        const input = litresForm.querySelector("#co2-volume");

        if (!input) {
            return;
        }

        const litres = parseFloat(input.value);

        if (!Number.isFinite(litres) || litres <= 0) {
            input.focus();

            return;
        }

        showResult(calculateFromLitres(litres));
    });

    /**
     * ------------------------------------------------------
     * FORMULAIRE DISTANCE
     * ------------------------------------------------------
     */

    distanceForm.addEventListener("submit", (event) => {
        event.preventDefault();

        const distanceInput = distanceForm.querySelector("#co2-distance");

        const consumptionInput = distanceForm.querySelector("#co2-consumption");

        if (!distanceInput || !consumptionInput) {
            return;
        }

        const distance = parseFloat(distanceInput.value.replace(",", "."));

        const consumption = parseFloat(consumptionInput.value.replace(",", "."));

        if (!Number.isFinite(distance) || distance <= 0) {
            distanceInput.focus();

            return;
        }

        if (!Number.isFinite(consumption) || consumption <= 0) {
            consumptionInput.focus();

            return;
        }

        showResult(calculateFromDistance(distance, consumption));
    });

    /**
     * ------------------------------------------------------
     * NORMALISATION CONSOMMATION
     * ------------------------------------------------------
     */

    const consumptionInput = simulator.querySelector("#co2-consumption");

    if (consumptionInput) {
        consumptionInput.addEventListener("input", () => {
            consumptionInput.value = consumptionInput.value.replace(",", ".").replace(/[^0-9.]/g, "");

            const parts = consumptionInput.value.split(".");

            if (parts.length > 2) {
                consumptionInput.value = `${parts.shift()}.${parts.join("")}`;
            }
        });
    }

    /**
     * ------------------------------------------------------
     * MESSAGES
     * ------------------------------------------------------
     */

    const clearFormMessage = () => {
        if (!formMessage) {
            return;
        }

        formMessage.textContent = "";

        formMessage.classList.add("hidden");

        formMessage.classList.remove("bg-green-light", "text-green-dark", "bg-red-100", "text-red-700");
    };

    const showFormMessage = (message, type = "error") => {
        if (!formMessage) {
            return;
        }

        clearFormMessage();

        formMessage.textContent = message;

        if (type === "success") {
            formMessage.classList.add("bg-green-light", "text-green-dark");
        } else {
            formMessage.classList.add("bg-red-100", "text-red-700");
        }

        formMessage.classList.remove("hidden");
    };

    /**
     * ------------------------------------------------------
     * ÉTAT SUBMIT
     * ------------------------------------------------------
     */

    const setSubmitting = (state) => {
        isSubmitting = state;

        if (emailSubmitButton) {
            emailSubmitButton.disabled = state;
        }

        if (submitLabel) {
            submitLabel.classList.toggle("hidden", state);
        }

        if (submitLoading) {
            submitLoading.classList.toggle("hidden", !state);
        }
    };

    /**
     * ------------------------------------------------------
     * MODALE
     * ------------------------------------------------------
     */

    const openModal = () => {
        if (!modal || !currentResult) {
            return;
        }

        clearFormMessage();

        modal.classList.remove("hidden");

        modal.classList.add("flex");

        modal.setAttribute("aria-hidden", "false");

        document.body.classList.add("overflow-hidden");

        window.setTimeout(() => {
            lastNameInput?.focus();
        }, 50);
    };

    const closeModal = () => {
        if (!modal || isSubmitting) {
            return;
        }

        modal.classList.add("hidden");

        modal.classList.remove("flex");

        modal.setAttribute("aria-hidden", "true");

        document.body.classList.remove("overflow-hidden");

        clearFormMessage();
    };

    if (downloadButton) {
        downloadButton.addEventListener("click", () => {
            if (!currentResult) {
                return;
            }

            openModal();
        });
    }

    modalCloseButtons.forEach((button) => {
        button.addEventListener("click", closeModal);
    });

    document.addEventListener("keydown", (event) => {
        if (event.key !== "Escape" || !modal || modal.classList.contains("hidden")) {
            return;
        }

        closeModal();
    });

    /**
     * ------------------------------------------------------
     * TÉLÉCHARGEMENT DU PDF
     * ------------------------------------------------------
     */

    const downloadPdf = (base64, filename) => {
        const binaryString = window.atob(base64);

        const length = binaryString.length;

        const bytes = new Uint8Array(length);

        for (let index = 0; index < length; index += 1) {
            bytes[index] = binaryString.charCodeAt(index);
        }

        const blob = new Blob([bytes], {
            type: "application/pdf",
        });

        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = url;

        link.download = filename || "bilan-co2-altens.pdf";

        document.body.appendChild(link);

        link.click();

        link.remove();

        window.setTimeout(() => {
            URL.revokeObjectURL(url);
        }, 1000);
    };

    /**
     * ------------------------------------------------------
     * ENVOI AJAX
     * ------------------------------------------------------
     */

    if (emailForm) {
        emailForm.addEventListener("submit", async (event) => {
            event.preventDefault();

            if (isSubmitting || !currentResult) {
                return;
            }

            clearFormMessage();

            /**
             * ------------------------------------------
             * DONNÉES PERSONNELLES
             * ------------------------------------------
             */

            const lastName = lastNameInput?.value.trim() || "";

            const firstName = firstNameInput?.value.trim() || "";

            const company = companyInput?.value.trim() || "";

            const email = emailInput?.value.trim() || "";

            /**
             * ------------------------------------------
             * VALIDATION NOM
             * ------------------------------------------
             */

            if (!lastName) {
                showFormMessage("Veuillez renseigner votre nom.");

                lastNameInput?.focus();

                return;
            }

            /**
             * ------------------------------------------
             * VALIDATION EMAIL
             * ------------------------------------------
             */

            if (!email) {
                showFormMessage("Veuillez renseigner votre adresse e-mail.");

                emailInput?.focus();

                return;
            }

            if (emailInput && !emailInput.checkValidity()) {
                showFormMessage("Veuillez renseigner une adresse e-mail valide.");

                emailInput.focus();

                return;
            }

            /**
             * ------------------------------------------
             * CONSENTEMENT
             * ------------------------------------------
             */

            if (!consentInput || !consentInput.checked) {
                showFormMessage("Veuillez accepter l’utilisation de vos informations pour recevoir votre bilan.");

                consentInput?.focus();

                return;
            }

            /**
             * ------------------------------------------
             * CONFIGURATION AJAX
             * ------------------------------------------
             */

            if (typeof altensCo2 === "undefined" || !altensCo2.ajaxUrl || !altensCo2.nonce) {
                showFormMessage("Le service est temporairement indisponible.");

                return;
            }

            /**
             * ------------------------------------------
             * DONNÉES ENVOYÉES À WORDPRESS
             * ------------------------------------------
             */

            const formData = new FormData();

            formData.append("action", "altens_generate_co2_pdf");

            formData.append("nonce", altensCo2.nonce);

            /**
             * Identité
             */

            formData.append("last_name", lastName);

            formData.append("first_name", firstName);

            formData.append("company", company);

            formData.append("email", email);

            formData.append("consent", "1");

            /**
             * Simulation
             */

            formData.append("mode", currentResult.mode);

            formData.append("input", currentResult.input);

            if (currentResult.mode === "distance" && currentResult.consumption) {
                formData.append("consumption", currentResult.consumption);
            }

            /**
             * ------------------------------------------
             * REQUÊTE AJAX
             * ------------------------------------------
             */

            setSubmitting(true);

            try {
                const response = await fetch(altensCo2.ajaxUrl, {
                    method: "POST",

                    credentials: "same-origin",

                    body: formData,
                });

                let data;

                try {
                    data = await response.json();
                } catch {
                    throw new Error("La réponse du serveur est invalide.");
                }

                if (!response.ok || !data.success) {
                    throw new Error(data?.data?.message || "Une erreur est survenue.");
                }

                if (!data.data?.pdf) {
                    throw new Error("Le fichier PDF n’a pas été reçu.");
                }

                /**
                 * Téléchargement.
                 */

                downloadPdf(data.data.pdf, data.data.filename);

                showFormMessage(data.data.message, "success");

                /**
                 * On ferme la modale
                 * après le lancement
                 * du téléchargement.
                 */

                window.setTimeout(() => {
                    closeModal();
                }, 900);
            } catch (error) {
                console.error("Erreur simulateur CO2 :", error);

                showFormMessage(error.message || "Une erreur est survenue. Veuillez réessayer.");
            } finally {
                setSubmitting(false);
            }
        });
    }

    /**
     * ------------------------------------------------------
     * RECOMMENCER
     * ------------------------------------------------------
     */

    if (restartButton) {
        restartButton.addEventListener("click", () => {
            currentResult = null;

            litresForm.reset();

            distanceForm.reset();

            if (emailForm) {
                emailForm.reset();
            }

            if (chart) {
                chart.destroy();

                chart = null;
            }

            closeModal();

            resultStep.classList.add("hidden");

            formStep.classList.remove("hidden");

            setMode("litres");

            simulator.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        });
    }

    /**
     * ------------------------------------------------------
     * ÉTAT INITIAL
     * ------------------------------------------------------
     */

    setMode("litres");
};

/**
 * ------------------------------------------------------
 * INITIALISATION
 * ------------------------------------------------------
 */

document.addEventListener("DOMContentLoaded", initCo2Simulator);
